import '@css/Yacht.css'

import { AppConfig, getPages, isPageDisabled } from '@config'
import { ROUTES } from '@constants/app_routes'
import type { YachtInformationTab } from '@constants/types'
import { useYisNavigate } from '@hooks/useYisNavigate'
import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import { isNumber } from 'lodash'
import type { CSSProperties } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Transition } from 'react-transition-group'
import type { TransitionStatus } from 'react-transition-group/Transition'

import { noModelSelected } from './admin/User/ThreeDModelSelector'

const timeout = 300

const Nav = styled(Grid)({
  width: '21vw',
  height: '1.25rem',
  left: '50%',
  position: 'fixed',
  bottom: '5rem',
  backgroundColor: 'var(--menu-bg)!important',
  transform: 'translateX(-50%)',
  borderRadius: '.5rem',
  zIndex: 100,
  overflow: 'initial!important',
  opacity: 'var(--layer-upper-opacity) !important'
})

const RouteName = styled(Typography)({
  position: 'absolute',
  fontFamily: 'var(--font-family)',
  bottom: '2rem',
  left: '50%',
  width: '20vw',
  transform: 'translateX(-50%)',
  opacity: 0,
  transition: `all ${timeout}ms ease`,
  pointerEvents: 'none',
  display: 'flex',
  justifyContent: 'center'
})

const SlideInput = styled('input')({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: '100%',
  borderRadius: 10,
  WebkitAppearance: 'none',
  appearance: 'none'
})

const transitionStyles: Record<TransitionStatus, CSSProperties> = {
  entering: { opacity: 1, bottom: 'calc(10% + 6rem)' },
  entered: { opacity: 1, bottom: 'calc(10% + 6rem)' },
  exiting: { opacity: 0, bottom: 'calc(10% + 5rem)' },
  exited: { opacity: 0, bottom: 'calc(10% + 5rem)' },
  unmounted: { opacity: 0, minWidth: 0 }
}

const MESSAGE_DURATION = 1000 * 5

export default function AboutYacht () {
  const { t } = useTranslation()

  const hasMedia = useCallback((tab: YachtInformationTab) => {
    if (tab.pageName === 'three_d') {
      return AppConfig['3d'].main() !== noModelSelected
    }
    return true
  }, [])

  const allTabs = useMemo<YachtInformationTab[]>(() => [
    {
      key: 'YachtInformation',
      label: t('Yacht'),
      to: ROUTES.YACHT_PAGE,
      pageName: 'yacht_page'
    },
    {
      key: 'MechanicalComponents',
      label: t('Mechanical Components'),
      to: ROUTES.DETAILS,
      pageName: 'details'
    },
    {
      key: 'SafetyPlan',
      label: t('Safety Plan'),
      to: ROUTES.SAFETY_PLAN,
      pageName: 'safety_plan'
    },
    {
      key: 'ThreeD',
      label: t('3D View'),
      to: ROUTES.THREE_D,
      pageName: 'three_d'
    }
  ], [t])

  const validPageNames = useMemo(() => getPages().map(p => p.page), [])

  const tabs = useMemo(() => allTabs.filter(tab => {
    return validPageNames.includes(tab.pageName) && !isPageDisabled(tab.key) && hasMedia(tab)
  }), [allTabs, hasMedia, validPageNames])

  const [range, setRange] = useState(0)
  const [maxValue, setMaxValue] = useState(400)
  const [currentRouteName, setCurrentRouteName] = useState('')
  const [value, setValue] = useState('0')

  const [showName, setShowName] = useState(false)
  const [messageId, setMessageId] = useState<NodeJS.Timeout | null>(null)

  const location = useLocation()
  const navigate = useYisNavigate()
  const [currentRoute, setCurrentRoute] = useState(1)
  const navRef = useRef<HTMLDivElement | null>(null)

  const handleRouteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!range) {
      return
    }

    const currentValue = +e.target.value
    let nextRoute = Math.floor(currentValue / range)

    if (nextRoute >= tabs.length) {
      nextRoute = tabs.length - 1
    }
    setCurrentRoute(previousRoute => previousRoute === nextRoute ? previousRoute : nextRoute)
    setValue(previousValue => previousValue === e.target.value ? previousValue : e.target.value)
  }

  const handleSetRangeThumb = useCallback((index: number, shouldShowLabel = false) => {
    if (!tabs.length) {
      return
    }

    const range = maxValue / tabs.length

    if (isNumber(range)) {
      let value = (index * maxValue) / tabs.length
      if (index) {
        value += (index * range) / (tabs.length - 1)
      }

      const nextValue = value.toString()
      setValue(previousValue => previousValue === nextValue ? previousValue : nextValue)
    }

    setCurrentRoute(previousRoute => previousRoute === index ? previousRoute : index)
    setRange(previousRange => previousRange === range ? previousRange : range)

    if (tabs[index]) {
      setCurrentRouteName(previousName => previousName === tabs[index].label ? previousName : tabs[index].label)
      if (shouldShowLabel) {
        setShowName(true)
      }
    }
  }, [maxValue, tabs])

  const handleOnMouseUp = () => {
    if (!tabs[currentRoute]) {
      return
    }

    navigate(tabs[currentRoute].to)
    handleSetRangeThumb(currentRoute, true)
  }

  const adjustMaxValue = useCallback(() => {
    const nextMaxValue = navRef.current?.offsetWidth

    if (nextMaxValue) {
      setMaxValue(previousMaxValue => previousMaxValue === nextMaxValue ? previousMaxValue : nextMaxValue)
    }
  }, [])

  const showMessageNow = () => {
    setShowName(true)
  }

  const hideMessageNow = () => {
    setShowName(false)
  }

  const hideMessageLater = () => {
    const id = setTimeout(hideMessageNow, MESSAGE_DURATION)
    setMessageId(id)
  }

  useEffect(() => {
    if (messageId) {
      clearTimeout(messageId)
      setMessageId(null)
    }

    if (showName) {
      hideMessageLater()
    }

    return () => {
      if (messageId) {
        clearTimeout(messageId)
        setMessageId(null)
      }
    }
  }, [showName])

  useEffect(() => {
    adjustMaxValue()

    const navElement = navRef.current
    if (!navElement || typeof ResizeObserver === 'undefined') {
      const timeoutId = setTimeout(adjustMaxValue, 15)
      return () => clearTimeout(timeoutId)
    }

    const observer = new ResizeObserver(adjustMaxValue)
    observer.observe(navElement)

    return () => {
      observer.disconnect()
    }
  }, [adjustMaxValue])

  useEffect(() => {
    const currentRouteIndex = tabs.findIndex(({ to }) => location.pathname.includes(to))
    if (maxValue && currentRouteIndex >= 0) {
      handleSetRangeThumb(currentRouteIndex)
    }
  }, [handleSetRangeThumb, location.pathname, maxValue, tabs])

  if (tabs.length <= 1) {
    return null
  }

  return <>
    <Transition in={showName} timeout={timeout}>
      {(state: TransitionStatus) => <RouteName style={{ ...transitionStyles[state] }} align="center" variant="h6">
        {currentRouteName}
      </RouteName>}
    </Transition>

    <Nav className="slide-nav" ref={navRef}>
      <Grid container>
        <SlideInput
          onMouseEnter={showMessageNow}
          onMouseLeave={hideMessageLater}
          type="range"
          className="slide"
          style={{ '--nav-slide-width': `${maxValue / tabs.length}px` } as CSSProperties}
          min={0}
          max={maxValue}
          onMouseUp={handleOnMouseUp}
          onTouchEnd={handleOnMouseUp}
          onChange={handleRouteChange}
          value={value}
        />
      </Grid>
    </Nav>
  </>
}
